import { AnyAction, combineReducers } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import thirdPartyApiReducer from "@/shared/redux/reducers/third-party-api";

// Persist configs
import {
  authPersistConfig,
  autofillPersistConfig,
  carPersistConfig,
  healthQuotePersistConfig,
  lifeQuotePersistConfig,
  masterCoveragePersistConfig,
  personalDetailsPersistConfig,
  quoteFlowPersistConfig,
  thirdPartyApiPersistConfig,
  travelQuotePersistConfig,
} from "./persistConfig";

// Core slices
import authSlice from "@/shared/redux/reducers/authSlice";

// Auth module slices
import clientSlice from "@/shared/redux/reducers/clientSlice";
import policySlice from "@/shared/redux/reducers/policySlice";
import quoteSlice from "@/shared/redux/reducers/quoteSlice";

// Master slices
import masterCoverageReducer from "@/shared/redux/reducers/masterCoverageSlice";
import masterSlice from "@/shared/redux/reducers/masterSlice";

// Feature slices
import carQuoteReducer from "@/D2C/features/car-quote/redux/car-quote/carQuoteSlice";
import { dynamicCarDropdownReducer } from "@/D2C/features/car-quote/redux/dynamic-dropdown";
import motorSumInsuredReducer from "@/D2C/features/car-quote/redux/motor-suminsured/motorSumInsuredSlice";
import healthQuoteReducer from "@/D2C/features/health-quote/redux/health-quote/healthQuoteSlice";
import lifeQuoteReducer from "@/D2C/features/life-quote/redux/lifeQuoteSlice";
import {
  default as travelQuoteReducer,
  default as travelReducer,
} from "@/D2C/features/travel-quote/redux/travel-quote/travelQuoteSlice";
import apiConfigSlice from "@/shared/redux/reducers/api-config";
import commonSlice from "@/shared/redux/reducers/commonSlice";
import insurerSlice from "@/shared/redux/reducers/insurer";
import logsSlice from "@/shared/redux/reducers/logs-monitoring";
import mobileCountryCodeReducer from "@/shared/redux/reducers/mobileCountryCodeSlice";
import personalDetailsReducer from "@/shared/redux/reducers/personalDetailsSlice";
import quoteAndAddonReducer from "@/shared/redux/reducers/quoteAndAddonSlice";
import quoteFlowReducer from "@/shared/redux/reducers/quoteFlowSlice";
import { uiReducer } from "@/shared/redux/reducers/uiSlice";
import { autoPersonalDetailsReducer } from "../reducers/autofillPersonalDetailsSlice";
import { commissionLedgerReducer } from "../reducers/commission-ledger";

const appReducer = combineReducers({
  // Core
  ui: uiReducer,
  autofillPersonalDetails: persistReducer(autofillPersistConfig, autoPersonalDetailsReducer),
  personalDetailsReducer,

  // Auth
  auth: persistReducer(authPersistConfig, authSlice),
  clientSlice: clientSlice,
  policySlice: policySlice,
  quoteSlice: quoteSlice,
  dynamicCarDropdown: dynamicCarDropdownReducer,

  // Master
  masterSlice: masterSlice,
  masterCoverage: persistReducer(masterCoveragePersistConfig, masterCoverageReducer),

  // Features
  travel: travelReducer,
  carQuote: persistReducer(carPersistConfig, carQuoteReducer),
  travelQuote: persistReducer(travelQuotePersistConfig, travelQuoteReducer),
  healthQuote: persistReducer(healthQuotePersistConfig, healthQuoteReducer),
  lifeQuote: persistReducer(lifeQuotePersistConfig, lifeQuoteReducer),
  motorSumInsured: motorSumInsuredReducer,
  quoteFlow: persistReducer(quoteFlowPersistConfig, quoteFlowReducer),
  sharedPersonalDetails: persistReducer(personalDetailsPersistConfig, personalDetailsReducer),

  // Common
  commonSlice: commonSlice,
  quoteAndAddon: quoteAndAddonReducer,
  mobileCountryCode: mobileCountryCodeReducer,
  apiConfigSlice: apiConfigSlice,
  thirdPartyApiSlice: persistReducer(thirdPartyApiPersistConfig, thirdPartyApiReducer),
  logsSlice: logsSlice,
  insurerSlice: insurerSlice,
  commissionLedger: commissionLedgerReducer,
});

const rootReducer = (state: ReturnType<typeof appReducer> | undefined, action: AnyAction) => {
  return appReducer(state, action);
};

export default rootReducer;

export type RootState = ReturnType<typeof appReducer>;
